import { Match } from 'effect';
import thePlatformProvider from './fairplay-providers/the-platform.ts';
import defaultProvider from './fairplay-providers/default.ts';
import axinomFairplayConfig from './fairplay-providers/axinom.ts';
import { isValidUrl } from '~/utils/validations.ts';
import logger from '~/services/logger';

function setupWidevine(player: any, drm: any) {
  const licenseUrl = drm?.widevine?.license_url;

  if (!isValidUrl(licenseUrl)) {
    logger.error(
      `setupWidevine: Missing or invalid Widevine license URL: ${licenseUrl}`
    );
    return {};
  }

  player.eme();
  return {
    keySystems: {
      'com.widevine.alpha': {
        url: licenseUrl,
        ...(drm?.widevine?.license_headers && {
          licenseHeaders: drm?.widevine?.license_headers,
        }),
      },
    },
    type: 'application/dash+xml',
  };
}

export function drmSetup(player: any, drm: any, type?: string) {
  return Match.value({ type, drm }).pipe(
    Match.when(
      { drm: (drm: any) => Object.keys(drm || {}).length === 0 },
      () => {
        return {};
      }
    ),
    Match.when({ type: 'application/dash+xml' }, ({ drm }) =>
      setupWidevine(player, drm)
    ),
    Match.when(
      {
        drm: (drm: any) => {
          const drmKeys = Object.keys(drm);
          return drmKeys.length === 1 && drmKeys.includes('widevine');
        },
      },
      ({ drm }) => setupWidevine(player, drm)
    ),
    Match.when(
      {
        drm: (drm: any) => drm?.fairplay?.provider === 'axinom',
      },
      ({ drm }) => axinomFairplayConfig(player, drm)
    ),
    Match.when(
      { drm: (drm: any) => drm?.fairplay?.provider === 'theplatform' },
      ({ drm }) => thePlatformProvider(player, drm)
    ),
    Match.when(
      {
        type: (type) => ['application/x-mpegURL', 'video/hls'].includes(type),
      },
      ({ drm }) => defaultProvider(player, drm)
    ),
    Match.orElse(() => {
      return {};
    })
  );
}

export function getDrmTypeForAnalytics(
  drm: Record<string, unknown> | null | undefined
): string {
  try {
    if (!drm || typeof drm !== 'object') return 'unknown';
    const keys = Object.keys(drm);
    const hasWidevine = keys.includes('widevine');
    const hasFairplay = keys.includes('fairplay');
    const fairplayProvider = (drm as any)?.fairplay?.provider;

    if (hasWidevine && hasFairplay) return 'widevine_fairplay';
    if (hasWidevine) return 'widevine';
    if (hasFairplay) {
      if (fairplayProvider === 'axinom') return 'fairplay_axinom';
      if (fairplayProvider === 'theplatform') return 'fairplay_theplatform';
      return 'fairplay';
    }
    return 'unknown';
  } catch (error: any) {
    logger.error(
      `getDrmTypeForAnalytics: Error determining DRM type: ${error.message}`
    );
    return 'unknown';
  }
}
